import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recipes } from '../lib/api';
import RecipeThumbnail from '../components/RecipeThumbnail';

function Recipes() {
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [categoryFilter, setCategoryFilter] = useState('');
  const [cuisineFilter, setCuisineFilter] = useState('');
  const [dietaryFilter, setDietaryFilter] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['recipes', search, categoryFilter, cuisineFilter, dietaryFilter],
    queryFn: () => recipes.list({
      search: search || undefined,
      category: categoryFilter || undefined,
      cuisine: cuisineFilter || undefined,
      dietary: dietaryFilter || undefined,
    }),
  });

  const toggleFavoriteMutation = useMutation({
    mutationFn: (id) => recipes.toggleFavorite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
    },
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="max-w-7xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">All Recipes</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {data?.pagination?.total || 0} recipes saved
          </p>
        </div>
        <Link to="/new" className="btn btn-primary">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Recipe
        </Link>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search recipes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="input w-auto min-w-[140px]"
        >
          <option value="">All Categories</option>
          <option value="main-course">Main Course</option>
          <option value="appetizer">Appetizer</option>
          <option value="side-dish">Side Dish</option>
          <option value="dessert">Dessert</option>
          <option value="breakfast">Breakfast</option>
          <option value="soup">Soup</option>
          <option value="salad">Salad</option>
          <option value="snack">Snack</option>
        </select>
        <select
          value={cuisineFilter}
          onChange={(e) => setCuisineFilter(e.target.value)}
          className="input w-auto min-w-[120px]"
        >
          <option value="">All Cuisines</option>
          <option value="italian">Italian</option>
          <option value="mexican">Mexican</option>
          <option value="asian">Asian</option>
          <option value="american">American</option>
          <option value="mediterranean">Mediterranean</option>
          <option value="indian">Indian</option>
          <option value="chinese">Chinese</option>
          <option value="japanese">Japanese</option>
          <option value="thai">Thai</option>
        </select>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`btn-icon ${viewMode === 'grid' ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`btn-icon ${viewMode === 'list' ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="8" y1="6" x2="21" y2="6" />
              <line x1="8" y1="12" x2="21" y2="12" />
              <line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" />
              <line x1="3" y1="12" x2="3.01" y2="12" />
              <line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg">
          {error.message || 'Failed to load recipes'}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && data?.recipes?.length === 0 && (
        <div className="text-center py-16">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {search ? 'No recipes found' : 'No recipes yet'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {search
              ? 'Try a different search term'
              : 'Extract your first recipe from a cooking video!'}
          </p>
          {!search && (
            <Link to="/new" className="btn btn-primary">
              Add Your First Recipe
            </Link>
          )}
        </div>
      )}

      {/* Recipe grid/list */}
      {!isLoading && !error && data?.recipes?.length > 0 && (
        <div
          className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
              : 'space-y-4'
          }
        >
          {data.recipes.map((recipe) => (
            <Link
              key={recipe.id}
              to={`/recipe/${recipe.id}`}
              className={`card overflow-hidden hover:shadow-md transition-shadow ${
                viewMode === 'list' ? 'flex' : ''
              }`}
            >
              {/* Thumbnail */}
              <div
                className={`${
                  viewMode === 'grid' ? 'aspect-video' : 'w-24 h-24 flex-shrink-0'
                }`}
              >
                <RecipeThumbnail
                  recipe={recipe}
                  size={viewMode === 'list' ? 'small' : 'default'}
                />
              </div>

              {/* Content */}
              <div className="p-4 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-2">
                    {recipe.title}
                  </h3>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      toggleFavoriteMutation.mutate(recipe.id);
                    }}
                    className={`flex-shrink-0 ${
                      recipe.isFavorite ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-500'
                    }`}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill={recipe.isFavorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </button>
                </div>

                {recipe.sourceCreator && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {recipe.sourceCreator}
                  </p>
                )}
                {/* Tags */}
                {(recipe.categories?.length > 0 || recipe.cuisines?.length > 0) && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {recipe.categories?.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2 py-0.5 bg-accent-50 text-accent-600 border border-dashed border-accent-300 rounded-full dark:bg-accent-900/20 dark:text-accent-400 dark:border-accent-700"
                        title="AI-detected category"
                      >
                        {tag.replace(/-/g, ' ')}
                      </span>
                    ))}
                    {recipe.cuisines?.slice(0, 1).map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 border border-dashed border-blue-300 rounded-full dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-700"
                        title="AI-detected cuisine"
                      >
                        {tag.replace(/-/g, ' ')}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
                  {recipe.totalTime && <span>{recipe.totalTime}</span>}
                  {recipe.servings && <span>{recipe.servings}</span>}
                </div>

                <div className="flex items-center justify-between mt-3">
                  <span className={`badge ${
                    recipe.confidence === 'high' ? 'badge-success' :
                    recipe.confidence === 'medium' ? 'badge-warning' :
                    'badge-error'
                  }`}>
                    {recipe.confidence} confidence
                  </span>
                  <span className="text-xs text-gray-400">
                    {formatDate(recipe.createdAt)}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {data?.pagination?.hasMore && (
        <div className="flex justify-center mt-8">
          <button className="btn btn-secondary">
            Load More
          </button>
        </div>
      )}
    </div>
  );
}

export default Recipes;
